import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const productGroups = [
  {
    label: "Brake Components Division",
    items: [
      "Commercial Vehicle Brake Pads",
      "Disc Pads",
      "Brake Linings",
      "Railway Brake Blocks",
      "Sintered Brake Pads",
      "Clutch Facings",
    ],
  },
  {
    label: "Rane Other Parts & Components",
    items: [
      "Engine Valves",
      "Water Pump",
      "Engine Mountings",
      "Suspension Bush Kit",
      "King Pin",
      "Gaskets",
    ],
  },
  {
    label: "Industrial & Off-Road Tyres",
    items: [
      "Solid Resilient Tyres",
      "Press-On Band Tyres",
      "Pneumatic Tyres",
      "Polyurethane Wheels",
      "Assemblies",
      "Wheels & Rims",
      "Off-Road / OTR / Agri Tyres",
    ],
  },
  {
    label: "Wheel Alignment, Balancing & Tyre Fitting",
    items: [
      "Wheel Alignment Machine",
      "Wheel Balancing Machine",
      "Tyre Fitting Machine",
      "Tyre Fitting Accessories",
    ],
  },
  {
    label: "Other",
    items: ["General Enquiry", "Bespoke / Custom Fitment"],
  },
];

const defaultProductInterest = productGroups[0].items[0];



export default function QuoteForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    vehicle: "",
    service: defaultProductInterest,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();

    if (!form.full_name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in name, email, phone and product interest.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "c82d73d6-3cb8-41e3-b862-0c3fb0cc2c5a",
          subject: "New Quote Request - OMC LTD",
          from_name: form.full_name,
          name: form.full_name,
          email: form.email,
          phone: form.phone,
          vehicle_fitment: form.vehicle,
          product_interest: form.service,
          quantity_notes: form.message,
          botcheck: "",
        }),
      });

      const result = await response.json();

      if (result.success) {
        setDone({ id: Date.now().toString() });
        toast.success("Quote request received. We'll be in touch shortly.");

        setForm({
          full_name: "",
          email: "",
          phone: "",
          vehicle: "",
          service: defaultProductInterest,
          message: "",
        });
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 md:py-32 bg-[#B8CCE0] border-y border-[#D5DEE8]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="overline-maroon mb-5">06 — Request a Quote</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-[#0F1E33]">
              Tell us about your{" "}
              <span className="italic text-[#1E3A5F]">requirement</span>.
            </h2>
            <div className="hairline-gold w-24 mt-6" />
              <p className="mt-6 text-[#1F2D40] leading-relaxed max-w-md text-base md:text-lg">
              Share part details and quantity. We'll respond within one business
              day with stock availability, lead time and bulk-tier pricing — no
              obligation.
            </p>
            <p className="mt-4 text-sm text-[#4A5A70] leading-relaxed max-w-md">
              If this form doesn't go through, email{" "}
              <a href="mailto:omcltd.eu@gmail.com" className="text-[#1E3A5F] underline-offset-4 hover:underline">omcltd.eu@gmail.com</a>
              {" "}or{" "}
              <a href="mailto:omc.ltd@1eo.uk" className="text-[#1E3A5F] underline-offset-4 hover:underline">omc.ltd@1eo.uk</a>
              {" "}for a faster response.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "Bulk and fleet pricing on enquiry",
                "Stock confirmed within 1 business day",
                "UK &amp; Europe logistics co-ordination",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#4A5A70]">
                  <CheckCircle size={20} weight="duotone" className="text-[#B8860B] mt-1" />
                  <span className="text-sm" dangerouslySetInnerHTML={{ __html: t }} />
                </li>
              ))}
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="bg-white border border-[#D5DEE8] p-8 md:p-10 relative shadow-sm">
              <div className="absolute -top-px left-0 w-24 h-px bg-[#B8860B]" />

              {done ? (
                <div data-testid="quote-success" className="py-10 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 border border-[#B8860B] text-[#1E3A5F] mb-6">
                    <CheckCircle size={32} weight="duotone" />
                  </div>
                  <h3 className="font-serif-display text-3xl text-[#0F1E33] mb-3">Request received.</h3>
                  <p className="text-[#4A5A70] max-w-md mx-auto">
                    Reference{" "}
                    <span className="font-mono text-[#1E3A5F]">
                      #{done.id?.slice(0, 8).toUpperCase()}
                    </span>
                    . An advisor will reach out within 24 hours.
                  </p>
                  <button
                    data-testid="quote-new-request"
                    onClick={() => setDone(null)}
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3 border border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-[#D6E2F0] transition-colors"
                  >
                    Submit another
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="quote-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Full Name" required>
                      <input data-testid="quote-input-name" className="formal-input" value={form.full_name} onChange={update("full_name")} placeholder="Workshop / buyer name" required />
                    </Field>
                    <Field label="Email" required>
                      <input data-testid="quote-input-email" type="email" className="formal-input" value={form.email} onChange={update("email")} placeholder="orders@company.com" required />
                    </Field>
                    <Field label="Phone" required>
                      <input data-testid="quote-input-phone" className="formal-input" value={form.phone} onChange={update("phone")} placeholder="+44 24 0000 0000" required />
                    </Field>
                    <Field label="Vehicle / Fitment">
                      <input data-testid="quote-input-vehicle" className="formal-input" value={form.vehicle} onChange={update("vehicle")} placeholder="e.g. Tata 1109 · Rear axle · 410mm" />
                    </Field>
                    <Field label="Product Interest" required className="md:col-span-2">
                      <select data-testid="quote-input-service" className="formal-input cursor-pointer" value={form.service} onChange={update("service")} required>
                        {productGroups.map((g) => (
                          <optgroup key={g.label} label={g.label} className="bg-white text-[#0F1E33]">
                            {g.items.map((s) => (
                              <option key={s} value={s} className="bg-white text-[#0F1E33]">{s}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </Field>
                    <Field label="Quantity & Notes" className="md:col-span-2">
                      <textarea data-testid="quote-input-message" className="formal-input resize-none" rows={4} value={form.message} onChange={update("message")} placeholder="Required quantity, delivery location, timeline…" />
                    </Field>
                  </div>

                  <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-[10px] text-[#7B8AA0] font-mono uppercase tracking-widest">
                      We respect your privacy · No spam, ever.
                    </p>
                    <button
                      type="submit"
                      data-testid="quote-submit-button"
                      disabled={submitting}
                      className="group inline-flex items-center gap-3 px-8 py-4 bg-[#1E3A5F] text-[#D6E2F0] font-medium tracking-wide hover:bg-[#15273F] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting ? "Sending…" : "Submit Request"}
                      <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`flex flex-col ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7B8AA0] mb-2">
        {label} {required && <span className="text-[#1E3A5F]">*</span>}
      </span>
      {children}
    </label>
  );
}
